// Main.jsx

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// Context
import { useNavigation } from 'context/NavigationContext';
import { useNodePeek } from 'context/NodePeekContext';
import { useTheme } from 'context/ThemeContext';
import { useUI } from 'context/UIContext';
import { useUser } from 'context/UserContext';
import { useBase } from 'context/BaseContext';
import { useBaseInviteAccept } from 'context/BaseInviteAcceptContext';
import { useBaseRoster } from 'context/BaseRosterContext';

// Routing
import { Outlet, useLocation } from 'react-router-dom';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Utilities
import { defaultFont } from 'utilities/Defaults';
import { PhotoShape, SectionType } from 'utilities/Enums';
import { loadFont } from 'utilities/Fonts';
import { inferSectionFromPath } from 'utilities/Routing';

// Services
import { updateSetting } from 'services/settings/SettingWriteService';

// Hooks
import usePreloadIcons from 'hooks/usePreloadIcons';
import usePreloadNodeEngagements from 'hooks/usePreloadNodeEngagements';
import useBaseAccessLock from 'hooks/useBaseAccessLock';
import useBaseSwitcher from 'hooks/useBaseSwitcher';
import useBaseVocabulary from 'hooks/useBaseVocabulary';
import useAiFeedDigest from 'spaces/hooks/useAiFeedDigest';
import useNodeSidebarRail from 'main/sidebar/hooks/useNodeSidebarRail';

// CSS
import './Main.css';

import BaseAddModal from 'bases/add/BaseAddModal';

// Components
import Button from 'components/button/Button';
import { MainStyles } from './MainStyles';
import SaveIndicator from 'components/saveindicator/SaveIndicator';
import Sidebar from 'main/sidebar/Sidebar';
import BaseRail from 'main/base-rail/BaseRail';
import ShellHeader from 'main/shell/ShellHeader';
import ShellPanel from 'main/shell/ShellPanel';
import GlobalToolbar from 'main/shell/GlobalToolbar';
import MobileTopBar from 'main/shell/MobileTopBar';
import SectionToolbar from 'main/shell/SectionToolbar';
import MobileMenu from 'main/menu/mobile/MobileMenu';
import UserButton from 'main/sidebar/user/UserButton';
import BaseCover from 'components/basecover/BaseCover';
import TrialExpiredPage from 'pages/trial/TrialExpiredPage';
import { getActiveBaseKeyFromSettings } from 'utilities/baseSettingsKeys';
import 'main/shell/ShellPanel.css';

/**
 * Main
 * 
 * Renders the main application component, including toolbar, menus, and content.
 * 
 * Responsively adapts layout for desktop, pad, and mobile views.
 * 
 * @returns {ReactNode} Main component.
 */
const Main = () => {

  const DEFAULT_DESKTOP_MENU_WIDTH = 250;
  const MIN_DESKTOP_MENU_WIDTH = 250;
  const MAX_DESKTOP_MENU_WIDTH = 420;
  const COMPACT_DESKTOP_MENU_WIDTH = 64;

  // ----- CONTEXT ----- //

  const { currentSection, setCurrentSection } = useNavigation();
  const {
    isSidebarRailCompact,
  } = useUI();

  // ----- HOOKS ----- //

  const { clearPeek, isPeekOpen } = useNodePeek();
  useNodeSidebarRail();

  // ----- CONTEXT ----- //

  // User Context

  const {
    user,
    userSettings,
    setUserSettings,
  } = useUser();

  // Base Context
  const { activeBase } = useBase();
  const { bases: rosterBases, hasLoaded: rosterHasLoaded } = useBaseRoster();
  const {
    hasPendingToken: hasPendingInviteToken,
    status: inviteAcceptStatus,
  } = useBaseInviteAccept();
  const { baseLabelPlural } = useBaseVocabulary();
  const { handleBaseSelect } = useBaseSwitcher();

  // Theme Context
  const { theme } = useTheme();

  usePreloadNodeEngagements();
  usePreloadIcons();
  useAiFeedDigest();

  // ----- HOOKS ----- //

  const { lockReason } = useBaseAccessLock();
  const shouldShowTrialExpiredView = lockReason === 'trial_expired';

  // ----- DEVICE DETECTION ----- //

  const isMobile = useIsMobile();

  const activeBaseKey = activeBase?.key || getActiveBaseKeyFromSettings(userSettings) || null;

  // ----- MEMOS ----- //

  const getClampedDesktopMenuWidth = useCallback((value) => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return DEFAULT_DESKTOP_MENU_WIDTH;
    return Math.min(MAX_DESKTOP_MENU_WIDTH, Math.max(MIN_DESKTOP_MENU_WIDTH, Math.round(numericValue)));
  }, []);

  const desktopMenuWidthFromSettings = useMemo(() => {
    if (!activeBaseKey) return DEFAULT_DESKTOP_MENU_WIDTH;

    const widthMap = userSettings?.DESKTOP_MENU_WIDTH_BY_BASE || userSettings?.DESKTOP_MENU_WIDTH_BY_WORKSPACE;
    const baseWidth = widthMap && typeof widthMap === 'object'
      ? widthMap[activeBaseKey]
      : null;

    return getClampedDesktopMenuWidth(baseWidth);
  }, [activeBaseKey, getClampedDesktopMenuWidth, userSettings?.DESKTOP_MENU_WIDTH_BY_BASE, userSettings?.DESKTOP_MENU_WIDTH_BY_WORKSPACE]);

  // ----- STATE ----- //

  const [desktopMenuWidth, setDesktopMenuWidth] = useState(desktopMenuWidthFromSettings);
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
  const [isResizeHandleHovered, setIsResizeHandleHovered] = useState(false);
  // First-workspace onboarding modal (required for cold signup).
  const [firstBaseModalOpen, setFirstBaseModalOpen] = useState(false);

  // ----- REFS ----- //

  const isResizingRef = useRef(false);
  const lastRouteSignatureRef = useRef('');
  const resizeStartXRef = useRef(0);
  const resizeStartWidthRef = useRef(DEFAULT_DESKTOP_MENU_WIDTH);
  const latestDesktopMenuWidthRef = useRef(desktopMenuWidthFromSettings);

  // ----- EFFECTS ----- //

  useEffect(() => {
    setDesktopMenuWidth(desktopMenuWidthFromSettings);
  }, [desktopMenuWidthFromSettings]);

  useEffect(() => {
    latestDesktopMenuWidthRef.current = desktopMenuWidth;
  }, [desktopMenuWidth]);

  // ----- MEMOS ----- //

  const persistDesktopMenuWidth = useCallback(async (nextWidth) => {
    if (!user?.key || !activeBaseKey) return;

    const widthMap = {
      ...(userSettings?.DESKTOP_MENU_WIDTH_BY_WORKSPACE || {}),
      ...(userSettings?.DESKTOP_MENU_WIDTH_BY_BASE || {}),
      [activeBaseKey]: getClampedDesktopMenuWidth(nextWidth),
    };

    try {
      await updateSetting(user.key, {
        DESKTOP_MENU_WIDTH_BY_BASE: widthMap,
      });

      setUserSettings && setUserSettings({
        ...(userSettings || {}),
        DESKTOP_MENU_WIDTH_BY_BASE: widthMap,
        userKey: user.key,
      });
    } catch (error) {
      console.error('[Main] failed to persist desktop menu width:', error);
    }
  }, [activeBaseKey, getClampedDesktopMenuWidth, setUserSettings, user?.key, userSettings]);

  // ----- HANDLERS ----- //

  const handleResizeMouseMove = useCallback((event) => {
    if (!isResizingRef.current) return;

    const deltaX = event.clientX - resizeStartXRef.current;
    const nextWidth = getClampedDesktopMenuWidth(resizeStartWidthRef.current + deltaX);
    setDesktopMenuWidth(nextWidth);
  }, [getClampedDesktopMenuWidth]);

  const handleResizeMouseUp = useCallback(() => {
    if (!isResizingRef.current) return;

    isResizingRef.current = false;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    window.removeEventListener('mousemove', handleResizeMouseMove);
    window.removeEventListener('mouseup', handleResizeMouseUp);

    const nextWidth = latestDesktopMenuWidthRef.current;
    if (nextWidth !== desktopMenuWidthFromSettings) {
      persistDesktopMenuWidth(nextWidth);
    }
  }, [desktopMenuWidthFromSettings, handleResizeMouseMove, persistDesktopMenuWidth]);

  const handleResizeMouseDown = useCallback((event) => {
    if (isMobile || isSidebarRailCompact) return;

    event.preventDefault();

    isResizingRef.current = true;
    resizeStartXRef.current = event.clientX;
    resizeStartWidthRef.current = latestDesktopMenuWidthRef.current;

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', handleResizeMouseMove);
    window.addEventListener('mouseup', handleResizeMouseUp);
  }, [handleResizeMouseMove, handleResizeMouseUp, isMobile, isSidebarRailCompact]);

  // ----- EFFECTS ----- //

  useEffect(() => () => {
    window.removeEventListener('mousemove', handleResizeMouseMove);
    window.removeEventListener('mouseup', handleResizeMouseUp);
  }, [handleResizeMouseMove, handleResizeMouseUp]);

  // ----- ROUTING ----- //

  // Routing

  const location = useLocation();

  // ----- EFFECTS ----- //

  useEffect(() => {
    if (!isMobile && isMobileDrawerOpen) {
      setIsMobileDrawerOpen(false);
    }
  }, [isMobile, isMobileDrawerOpen]);

  useEffect(() => {
    setIsMobileDrawerOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const routeSignature = `${location.pathname}${location.search}${location.hash}`;

    if (!lastRouteSignatureRef.current) {
      lastRouteSignatureRef.current = routeSignature;
      return;
    }

    if (lastRouteSignatureRef.current === routeSignature) {
      return;
    }

    lastRouteSignatureRef.current = routeSignature;

    if (isPeekOpen) {
      clearPeek();
    }
  }, [clearPeek, isPeekOpen, location.hash, location.pathname, location.search]);

  useEffect(() => {
    if (!isMobileDrawerOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isMobileDrawerOpen]);

  useEffect(() => {
    if (!isMobileDrawerOpen) return undefined;

    /**
     * handleKeyDown
     *
     * Handles a key down event.
     *
     * @param {object} event - event.
     */
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsMobileDrawerOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isMobileDrawerOpen]);

  useEffect(() => {
    const inferredSection = inferSectionFromPath(location.pathname);
    if (inferredSection && inferredSection !== currentSection) {
      setCurrentSection(inferredSection);
      return;
    }

    if (!inferredSection && location.pathname === '/' && currentSection !== SectionType.HOME) {
      setCurrentSection(SectionType.HOME);
    }
  }, [currentSection, location.pathname, setCurrentSection]);

  useEffect(() => {
    if (!user) return;
    loadFont(user.fontFamily || defaultFont);
  }, [user]);

  const needsFirstBase = Boolean(
    user
    && rosterHasLoaded
    && (!Array.isArray(rosterBases) || rosterBases.length === 0)
  );
  // Invite accept in progress — do not force create-workspace onboarding.
  const inviteJoinInFlight = Boolean(
    hasPendingInviteToken
    && inviteAcceptStatus !== 'error'
  );
  // Cold signup / invite failed: require naming a first workspace (trial base).
  const forceFirstBaseOnboarding = Boolean(needsFirstBase && !inviteJoinInFlight);
  const shellReady = Boolean(user);
  const isNodeFocusLayout = location.pathname.startsWith('/node/');

  useEffect(() => {
    if (forceFirstBaseOnboarding) {
      setFirstBaseModalOpen(true);
      return;
    }
    if (!needsFirstBase) {
      setFirstBaseModalOpen(false);
    }
  }, [forceFirstBaseOnboarding, needsFirstBase]);

  const hasBaseCover = useMemo(() => {
    return typeof activeBase?.sidebarCoverPhotoUrl === 'string'
      && activeBase.sidebarCoverPhotoUrl.trim().length > 0;
  }, [activeBase?.sidebarCoverPhotoUrl]);

  const shellMenuWidth = isSidebarRailCompact
    ? COMPACT_DESKTOP_MENU_WIDTH
    : desktopMenuWidth;
  const shellCoverHeight = shellMenuWidth * 0.75;

  const shellHeaderCover = (hasBaseCover && !isSidebarRailCompact) ? (
    <div className="sidebar-header-cover">
      <BaseCover
        assetType="cover"
        base={activeBase}
        borderWidth={0}
        borderRadius={7}
        height={shellCoverHeight}
        maxHeight={shellCoverHeight}
        shape={PhotoShape.SQUARE}
        width="100%"
      />
    </div>
  ) : null;

  // ----- RENDER ----- //

  return (
    <>
      <MainStyles
        highlight={theme.highlight}
        fontFamily={defaultFont}
        selectionBackground={theme.selectionBackground}
        selectionForeground={theme.selectionForeground}
      />

      <BaseAddModal
        isOpen={firstBaseModalOpen}
        required={forceFirstBaseOnboarding}
        setOpen={setFirstBaseModalOpen}
        onSave={(base) => {
          if (base?.key) handleBaseSelect(base);
        }}
      />

      <div
        className={[
          'main-container',
          isPeekOpen ? 'is-peek-open' : '',
        ].filter(Boolean).join(' ')}
        style={{
          backgroundColor: isMobile ? theme.backgroundMobile : theme.background
        }}
      >

        {/* BACKGROUND — temporarily disabled
        <Background node={background || null} />
        */}

        {/* CONTENT LAYER */}
        <div className="main-content-layer">

          <>
              {!isMobile && (
                <>
                  {shellReady ? (
                    <div className="main-desktop-shell">
                      {/** BASE RAIL 
                      <BaseRail />
                      */}

                      {activeBase ? (

                        <div
                          className={[
                            'main-desktop-menu',
                            isSidebarRailCompact ? 'is-rail-compact' : '',
                          ].filter(Boolean).join(' ')}
                          style={{
                            width: shellMenuWidth,
                          }}
                        >

                          <ShellPanel
                            header={(
                              <ShellHeader
                                beforeSwitcher={shellHeaderCover}
                              />
                            )}
                          >
                            <Sidebar
                              hideHeader
                              coverHeight={shellCoverHeight}
                            />
                          </ShellPanel>

                          <div
                            className={[
                              'shell-user-panel',
                              isSidebarRailCompact ? 'is-rail-compact' : '',
                            ].filter(Boolean).join(' ')}
                            aria-label="Account"
                          >
                            <UserButton />
                          </div>

                          {/* DESKTOP MENU RESIZE HANDLE */}
                          {!isSidebarRailCompact ? (
                            <div
                              className="main-desktop-menu-resize-handle"
                              role="separator"
                              aria-orientation="vertical"
                              aria-label="Resize desktop menu"
                              onMouseDown={handleResizeMouseDown}
                              onMouseEnter={() => setIsResizeHandleHovered(true)}
                              onMouseLeave={() => setIsResizeHandleHovered(false)}
                            >

                              {/* DESKTOP MENU RESIZE HANDLE TRACK */}
                              <div
                                className="main-desktop-menu-resize-handle-track"
                                style={{
                                  backgroundColor: isResizeHandleHovered ? theme.backgroundHover : "transparent",
                                }}
                              />
                            </div>
                          ) : null}
                        </div>
                      ) : needsFirstBase ? (

                        <div className="main-desktop-menu-loading">
                          <div className="main-empty-roster-panel">
                            <div
                              className="main-empty-roster-title"
                              style={{ color: theme.foreground }}
                            >
                              {inviteJoinInFlight
                                ? 'Joining your workspace…'
                                : `Set up your ${(baseLabelPlural || 'bases').toLowerCase()}`}
                            </div>
                            <div
                              className="main-empty-roster-copy"
                              style={{ color: theme.foregroundMuted }}
                            >
                              {inviteJoinInFlight
                                ? 'Accepting your invite. This only takes a moment.'
                                : 'Name your first knowledge base to start a free trial.'}
                            </div>
                            {!inviteJoinInFlight ? (
                              <Button
                                label="Name your workspace"
                                onClick={() => setFirstBaseModalOpen(true)}
                              />
                            ) : null}
                          </div>
                        </div>
                      ) : (

                        <div
                          className="main-desktop-menu-loading"
                        >
                          <div style={{ padding: 24, maxWidth: 280 }}>
                            <div style={{
                              color: theme.foregroundMuted,
                              fontSize: 13,
                              lineHeight: 1.45,
                            }}
                            >
                              Loading your space…
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : null}
                </>
              )}

              {shellReady && needsFirstBase && !activeBase ? (
                <div className="main-right">
                  <div className={`main-middle${isMobile ? ' mobile-layout' : ''}`}>
                    <div className={`main-content${isMobile ? ' mobile' : ''}`}>
                      <div className="main-empty-roster-hero">
                        <div
                          className="main-empty-roster-title"
                          style={{ color: theme.foreground }}
                        >
                          Welcome to Appdeck
                        </div>
                        <div
                          className="main-empty-roster-copy"
                          style={{ color: theme.foregroundMuted }}
                        >
                          {inviteJoinInFlight
                            ? 'We’re adding you to the workspace you were invited to.'
                            : 'Name your first knowledge base to get started — it includes a free trial.'}
                        </div>
                        {!inviteJoinInFlight ? (
                          <Button
                            label="Name your workspace"
                            onClick={() => setFirstBaseModalOpen(true)}
                          />
                        ) : null}
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}

              {shellReady && activeBase && (
                <>
                <div
                  className={[
                    'main-right',
                    isNodeFocusLayout ? 'is-node-focus' : '',
                  ].filter(Boolean).join(' ')}
                >

                  {isMobile && (
                    <>
                      {/* OVERLAY — dismiss on outside tap */}
                      <div
                        className={`main-mobile-drawer-overlay${isMobileDrawerOpen ? ' open' : ''}`}
                        aria-hidden={!isMobileDrawerOpen}
                        onClick={() => setIsMobileDrawerOpen(false)}
                      />

                      {/* MOBILE DRAWER */}
                      <aside
                        id="main-mobile-drawer"
                        className={`main-mobile-drawer${isMobileDrawerOpen ? ' open' : ''}`}
                        role="dialog"
                        aria-modal={isMobileDrawerOpen ? 'true' : undefined}
                        aria-label="Sidebar menu"
                        aria-hidden={!isMobileDrawerOpen}
                      >
                        <Sidebar
                          forceVisible={true}
                          hideAddButton={false}
                          hideTemplatesButton={true}
                          onRequestClose={() => setIsMobileDrawerOpen(false)}
                        />
                      </aside>

                      {/* MOBILE TOP BAR — menu + find (in flow) */}
                      <MobileTopBar
                        isDrawerOpen={isMobileDrawerOpen}
                        setIsDrawerOpen={setIsMobileDrawerOpen}
                      />
                    </>
                  )}

                  {/* GLOBAL TOOLBAR */}
                  {!isMobile ? <GlobalToolbar /> : null}

                  {/* SECTION TOOLBAR — title + section actions */}
                  {!isMobile ? <SectionToolbar /> : null}

                  {isNodeFocusLayout ? (
                    <div className={`main-node-fill${isMobile ? ' mobile' : ''}`}>
                      {shouldShowTrialExpiredView ? (
                        <TrialExpiredPage embedded />
                      ) : (
                        <Outlet />
                      )}
                    </div>
                  ) : (
                    <div className={`main-middle${isMobile ? ' mobile-layout' : ''}`}>

                      {/* CONTENT */}
                      <div className={`main-content${isMobile ? ' mobile' : ''}`}>
                        {shouldShowTrialExpiredView ? (
                          <TrialExpiredPage embedded />
                        ) : (
                          <Outlet />
                        )}
                      </div>

                    </div>
                  )}

                  {isMobile ? (
                    <div className="main-mobile-menu">
                      <MobileMenu />
                    </div>
                  ) : null}
                </div>

                {/* Desktop peek docks here as a flex sibling (pushes main-right). */}
                {!isMobile ? (
                  <div
                    id="main-peek-slot"
                    className="main-peek-slot"
                  />
                ) : null}
                </>
              )}
          </>

          <SaveIndicator />

        </div>

      </div >
    </>
  );
};

// Dev Tools
Main.displayName = 'Main';

export default Main;
